import { useState, type ReactNode } from 'react';
import { Button, Dialog, EmptyState, Input, Skeleton } from '../ui';

type LoadingStateProps = {
  label?: string;
  rows?: number;
};

export function LoadingState({ label = 'Loading', rows = 3 }: LoadingStateProps) {
  return (
    <section className="panel" aria-busy="true" aria-label={label}>
      <div className="panel-body">
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton key={index} />
        ))}
      </div>
    </section>
  );
}

type EmptyStateBlockProps = {
  index?: string;
  title: string;
  detail: string;
  action?: ReactNode;
};

export function EmptyStateBlock(props: EmptyStateBlockProps) {
  return <EmptyState {...props} />;
}

type ErrorStateProps = {
  title?: string;
  detail: string;
  onRetry?: () => void;
};

export function ErrorState({ title = 'Something went wrong', detail, onRetry }: ErrorStateProps) {
  return (
    <EmptyState
      index="ERROR / 01"
      title={title}
      detail={detail}
      action={
        onRetry ? (
          <Button type="button" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    />
  );
}

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  /** The exact word the operator must type to arm the action. */
  confirmWord: string;
  detail: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Destructive confirm. The action stays disabled until the operator types the
 * confirm word, so an accidental click cannot delete or revoke anything.
 */
export function ConfirmDialog({
  open,
  title,
  confirmWord,
  detail,
  confirmLabel = 'Delete',
  onConfirm,
  onClose
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  if (!open) return null;
  const armed = typed.trim() === confirmWord;

  return (
    <Dialog open={open} title={title} onClose={onClose}>
      <p>{detail}</p>
      <label className="index-label" htmlFor="confirm-word">
        TYPE {confirmWord.toUpperCase()} TO CONFIRM
      </label>
      <Input id="confirm-word" value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" />
      <div className="dialog-actions">
        <Button variant="quiet" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          type="button"
          disabled={!armed}
          onClick={() => {
            if (!armed) return;
            onConfirm();
            setTyped('');
          }}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
